import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  savePersonalInfo,
  selectApplication,
  selectAppSaving,
  selectAppError,
  clearApplicationError
} from '../../redux/slices/applicationSlice';
import { StepCard, StepActions, Button, FieldGrid, SectionRule } from '../../components/stepCard';
import { Field, TextField, SelectField, TextAreaField } from '../../components/formField';
import ImportPanel from '../../components/importPanel';
import PageTransition from '../../components/pageTransition';
import { notify } from '../../utils/notify';
// import { extractFromPdf, clearExtraction } from '../../../redux/slices/applicationSlice';

const GENDER_OPTIONS = [
  { value: 'MALE', label: 'Male' },
  { value: 'FEMALE', label: 'Female' },
  { value: 'OTHER', label: 'Other' }
];

const RESIDENCE_OPTIONS = [
  { value: 'RENTED', label: 'Rented' },
  { value: 'OWNED', label: 'Owned' },
  { value: 'COMPANY_PROVIDED', label: 'Company provided' }
];

// Client-side rules mirror the server's. The server is authoritative —
// this exists to give feedback before a round trip, not to replace it.
const validate = (values) => {
  const errors = {};

  if (!values.firstName?.trim()) errors.firstName = 'Enter your first name';
  if (!values.lastName?.trim()) errors.lastName = 'Enter your last name';

  if (!values.dateOfBirth) {
    errors.dateOfBirth = 'Enter your date of birth';
  } else {
    const dob = new Date(values.dateOfBirth);
    const now = new Date();
    let age = now.getFullYear() - dob.getFullYear();
    const m = now.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age -= 1;

    if (age < 21) errors.dateOfBirth = 'Applicants must be at least 21';
    else if (age > 65) errors.dateOfBirth = 'Applicants must be 65 or under';
  }

  if (!values.gender) errors.gender = 'Select your gender';

  if (!/^[6-9]\d{9}$/.test(values.mobileNumber || '')) {
    errors.mobileNumber = 'Enter a 10-digit mobile number starting 6–9';
  }

  if (values.contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.contactEmail)) {
    errors.contactEmail = 'Enter a valid email address';
  }

  if (!values.currentAddress?.trim()) errors.currentAddress = 'Enter your address';
  if (!values.city?.trim()) errors.city = 'Enter your city';
  if (!values.state?.trim()) errors.state = 'Enter your state';

  if (!/^[1-9][0-9]{5}$/.test(values.pinCode || '')) {
    errors.pinCode = 'Enter a 6-digit PIN code';
  }

  if (!values.residenceType) errors.residenceType = 'Select your residence type';

  return errors;
};

// Prisma returns an ISO timestamp; <input type="date"> needs YYYY-MM-DD.
const toDateInput = (iso) => (iso ? iso.split('T')[0] : '');

const PersonalInfoStep = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const application = useSelector(selectApplication);
  const saving = useSelector(selectAppSaving);
  const serverError = useSelector(selectAppError);

  const [values, setValues] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    mobileNumber: '',
    contactEmail: '',
    currentAddress: '',
    city: '',
    state: '',
    pinCode: '',
    residenceType: ''
  });
  const [errors, setErrors] = useState({});
  const [extractedKeys, setExtractedKeys] = useState(new Set());

  // Fields this step owns. Extraction may return more (KYC fields);
  // we take only what belongs here and carry the rest via Redux for
  // the KYC step to pick up.
  const PERSONAL_KEYS = [
    'firstName',
    'lastName',
    'dateOfBirth',
    'gender',
    'mobileNumber',
    'contactEmail',
    'currentAddress',
    'city',
    'state',
    'pinCode'
  ];

  const handleExtracted = (fields) => {
    const applied = new Set();

    setValues((prev) => {
      const next = { ...prev };
      PERSONAL_KEYS.forEach((key) => {
        if (fields[key] != null && fields[key] !== '') {
          next[key] = key === 'dateOfBirth' ? toDateInput(fields[key]) : String(fields[key]);
          applied.add(key);
        }
      });
      return next;
    });

    setExtractedKeys(applied);

    // Run validation immediately so a bad extraction shows its error now.
    setTimeout(() => {
      setValues((current) => {
        setErrors(validate(current));
        return current;
      });
    }, 0);
  };

  // Hydrate from the server once the draft loads.
  useEffect(() => {
    if (!application) return;
    setValues({
      firstName: application.firstName ?? '',
      lastName: application.lastName ?? '',
      dateOfBirth: toDateInput(application.dateOfBirth),
      gender: application.gender ?? '',
      mobileNumber: application.mobileNumber ?? '',
      contactEmail: application.contactEmail ?? '',
      currentAddress: application.currentAddress ?? '',
      city: application.city ?? '',
      state: application.state ?? '',
      pinCode: application.pinCode ?? '',
      residenceType: application.residenceType ?? ''
    });
  }, [application]);

  const setField = (name) => (e) => {
    setValues((prev) => ({ ...prev, [name]: e.target.value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
    if (serverError) dispatch(clearApplicationError());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const found = validate(values);
    setErrors(found);
    if (Object.keys(found).length) return;

    const result = await dispatch(savePersonalInfo({ id: application.id, values }));

    if (savePersonalInfo.fulfilled.match(result)) {
      navigate('/apply/kyc');
    } else {
      notify.error(result.payload);
    }
  };

  return (
    <PageTransition>
      <ImportPanel onExtracted={handleExtracted} />

      <StepCard
        title="Personal information"
        description="Enter your details exactly as they appear on your government ID.">
        <form onSubmit={handleSubmit} noValidate>
          <FieldGrid>
            <Field
              label="First name"
              htmlFor="firstName"
              required
              error={errors.firstName}
              extracted={extractedKeys.has('firstName')}>
              <TextField
                id="firstName"
                value={values.firstName}
                onChange={setField('firstName')}
                error={errors.firstName}
                autoComplete="given-name"
              />
            </Field>

            <Field
              label="Last name"
              htmlFor="lastName"
              required
              error={errors.lastName}
              extracted={extractedKeys.has('lastName')}>
              <TextField
                id="lastName"
                value={values.lastName}
                onChange={setField('lastName')}
                error={errors.lastName}
                autoComplete="family-name"
              />
            </Field>

            <Field
              label="Date of birth"
              htmlFor="dateOfBirth"
              required
              error={errors.dateOfBirth}
              extracted={extractedKeys.has('dateOfBirth')}>
              <TextField
                id="dateOfBirth"
                type="date"
                value={values.dateOfBirth}
                onChange={setField('dateOfBirth')}
                error={errors.dateOfBirth}
              />
            </Field>

            <Field
              label="Gender"
              htmlFor="gender"
              required
              error={errors.gender}
              extracted={extractedKeys.has('gender')}>
              <SelectField
                id="gender"
                value={values.gender}
                onChange={setField('gender')}
                error={errors.gender}
                placeholder="Select"
                options={GENDER_OPTIONS}
              />
            </Field>

            <Field
              label="Mobile number"
              htmlFor="mobileNumber"
              required
              hint="10 digits, starting with 6–9"
              error={errors.mobileNumber}
              extracted={extractedKeys.has('mobileNumber')}>
              <TextField
                id="mobileNumber"
                inputMode="numeric"
                maxLength={10}
                value={values.mobileNumber}
                onChange={setField('mobileNumber')}
                error={errors.mobileNumber}
                autoComplete="tel-national"
              />
            </Field>

            <Field
              label="Contact email"
              htmlFor="contactEmail"
              hint="Optional — where we send updates"
              error={errors.contactEmail}
              extracted={extractedKeys.has('contactEmail')}>
              <TextField
                id="contactEmail"
                type="email"
                value={values.contactEmail}
                onChange={setField('contactEmail')}
                error={errors.contactEmail}
                autoComplete="email"
              />
            </Field>

            <SectionRule label="Address" />

            <div className="field field--full">
              <Field
                label="Current address"
                htmlFor="currentAddress"
                required
                error={errors.currentAddress}
                extracted={extractedKeys.has('currentAddress')}>
                <TextAreaField
                  id="currentAddress"
                  value={values.currentAddress}
                  onChange={setField('currentAddress')}
                  error={errors.currentAddress}
                  autoComplete="street-address"
                />
              </Field>
            </div>

            <Field
              label="City"
              htmlFor="city"
              required
              error={errors.city}
              extracted={extractedKeys.has('city')}>
              <TextField
                id="city"
                value={values.city}
                onChange={setField('city')}
                error={errors.city}
                autoComplete="address-level2"
              />
            </Field>

            <Field
              label="State"
              htmlFor="state"
              required
              error={errors.state}
              extracted={extractedKeys.has('state')}>
              <TextField
                id="state"
                value={values.state}
                onChange={setField('state')}
                error={errors.state}
                autoComplete="address-level1"
              />
            </Field>

            <Field
              label="PIN code"
              htmlFor="pinCode"
              required
              error={errors.pinCode}
              extracted={extractedKeys.has('pinCode')}>
              <TextField
                id="pinCode"
                inputMode="numeric"
                maxLength={6}
                value={values.pinCode}
                onChange={setField('pinCode')}
                error={errors.pinCode}
                autoComplete="postal-code"
              />
            </Field>

            <Field
              label="Residence type"
              htmlFor="residenceType"
              required
              error={errors.residenceType}>
              <SelectField
                id="residenceType"
                value={values.residenceType}
                onChange={setField('residenceType')}
                error={errors.residenceType}
                placeholder="Select"
                options={RESIDENCE_OPTIONS}
              />
            </Field>
          </FieldGrid>

          {serverError && (
            <p className="form-error" role="alert">
              {serverError}
            </p>
          )}

          <StepActions>
            <Button type="submit" loading={saving}>
              {saving ? 'Saving…' : 'Continue to KYC →'}
            </Button>
          </StepActions>
        </form>
      </StepCard>
    </PageTransition>
  );
};

export default PersonalInfoStep;
